/**
 * PrivacyPage - Integritetspolicy
 *
 * SEO Target: /integritetspolicy
 * Legal page - GDPR compliance
 */

import { Link } from "react-router-dom";
import { PageMeta, getBreadcrumbSchema, SEO_CONFIG } from "@/lib/seo";
import { Breadcrumbs } from "@/layouts/PublicLayout";
import { Helmet } from "react-helmet-async";
import { Shield, ChevronRight } from "lucide-react";
import { useCompanyInfo } from "@/hooks/use-company-info";

export function PrivacyPage() {
  const lastUpdated = "2026-02-01";
  const { company, formattedCityOnly } = useCompanyInfo();

  // Format org number for display
  const orgNumberDisplay = company.orgNumber || "559XXX-XXXX";

  const breadcrumbItems = [{ label: "Integritetspolicy" }];

  const breadcrumbSchema = getBreadcrumbSchema([
    { name: "Hem", url: "/" },
    { name: "Integritetspolicy", url: "/integritetspolicy" },
  ]);

  const sections = [
    { id: "ansvarig", title: "1. Personuppgiftsansvarig" },
    { id: "insamling", title: "2. Personuppgifter vi Samlar in" },
    { id: "anvandning", title: "3. Hur vi Använder dina Uppgifter" },
    { id: "delning", title: "4. Hur vi Delar dina Uppgifter" },
    { id: "tredjeland", title: "5. Överföring till Tredje Land" },
    { id: "rattigheter", title: "6. Dina Rättigheter" },
    { id: "cookies", title: "7. Cookies" },
    { id: "sakerhet", title: "8. Säkerhet" },
    { id: "minderariga", title: "9. Minderåriga" },
    { id: "andringar", title: "10. Ändringar" },
    { id: "kontakt", title: "11. Kontakt" },
  ];

  return (
    <>
      <PageMeta
        title="Integritetspolicy - Kvanten"
        description="Läs hur Kvanten hanterar dina personuppgifter enligt GDPR. Information om datainsamling, lagring, dina rättigheter och hur du kontaktar oss."
        canonicalUrl={`${SEO_CONFIG.siteUrl}/integritetspolicy`}
        fullTitle
      />

      <Helmet>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      {/* Hero */}
      <section className="bg-gradient-to-b from-gray-50 to-white py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumbs items={breadcrumbItems} />

          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-3xl lg:text-4xl font-bold text-gray-900">Integritetspolicy</h1>
              <p className="text-gray-500">Senast uppdaterad: {lastUpdated}</p>
            </div>
          </div>

          <p className="text-lg text-gray-600">
            Denna integritetspolicy beskriver hur Kvanten samlar in, använder och skyddar dina
            personuppgifter. Vi värnar om din integritet och följer EU:s dataskyddsförordning (GDPR).
          </p>
        </div>
      </section>

      {/* Content with sidebar */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-4 lg:gap-12">
            {/* Table of contents - Sidebar */}
            <aside className="hidden lg:block">
              <div className="sticky top-24 bg-gray-50 rounded-xl p-6">
                <h2 className="font-semibold text-gray-900 mb-4">Innehåll</h2>
                <nav className="space-y-2">
                  {sections.map((section) => (
                    <a
                      key={section.id}
                      href={`#${section.id}`}
                      className="flex items-center gap-2 text-sm text-gray-600 hover:text-blue-600 transition-colors py-1"
                    >
                      <ChevronRight className="w-3 h-3" />
                      {section.title}
                    </a>
                  ))}
                </nav>
              </div>
            </aside>

            {/* Main content */}
            <article className="lg:col-span-3 prose prose-gray max-w-none">
              <p className="lead">
                {company.name}{orgNumberDisplay ? `, org.nr ${orgNumberDisplay}` : ""} ("Kvanten", "vi", "oss") samlar in, använder och
                skyddar dina personuppgifter när du använder vår tjänst. Vi följer EU:s
                dataskyddsförordning (GDPR) samt svensk dataskyddslagstiftning.
              </p>

              <section id="ansvarig">
                <h2>1. Personuppgiftsansvarig</h2>
                <p>
                  {company.name} är personuppgiftsansvarig för behandlingen av dina personuppgifter.
                </p>
                <div className="bg-gray-50 rounded-lg p-4 not-prose">
                  <p className="text-gray-700 font-medium">{company.name}</p>
                  <p className="text-gray-600 mt-1">{formattedCityOnly}</p>
                  <p className="text-gray-600 mt-1">
                    E-post:{" "}
                    <a href={`mailto:${company.email}`} className="text-blue-600 hover:underline">
                      {company.email}
                    </a>
                  </p>
                </div>
              </section>

              <section id="insamling">
                <h2>2. Vilka Personuppgifter vi Samlar in</h2>

                <h3>2.1 Uppgifter du lämnar till oss</h3>
                <ul>
                  <li>
                    <strong>Kontoinformation:</strong> Namn, e-postadress, telefonnummer, lösenord
                    (krypterat)
                  </li>
                  <li>
                    <strong>Företagsinformation:</strong> Organisationsnummer, företagsnamn, adress
                  </li>
                  <li>
                    <strong>Bokföringsdata:</strong> SIE-filer med kontouppgifter, transaktioner och
                    saldon
                  </li>
                  <li>
                    <strong>Styrelse och ägare:</strong> Namn, personnummer, roller, aktieinnehav
                  </li>
                  <li>
                    <strong>Betalningsuppgifter:</strong> Faktureringsadress, betalningshistorik
                    (kortuppgifter hanteras av Stripe)
                  </li>
                </ul>

                <h3>2.2 Uppgifter vi samlar in automatiskt</h3>
                <ul>
                  <li>
                    <strong>Teknisk information:</strong> IP-adress, webbläsartyp, operativsystem,
                    enhetsinformation
                  </li>
                  <li>
                    <strong>Användningsdata:</strong> Inloggningshistorik, sidvisningar,
                    funktionsanvändning
                  </li>
                  <li>
                    <strong>Cookies:</strong> Se{" "}
                    <a href="#cookies" className="text-blue-600 hover:underline">
                      avsnitt 7
                    </a>{" "}
                    om cookies
                  </li>
                </ul>
              </section>

              <section id="anvandning">
                <h2>3. Hur vi Använder dina Uppgifter</h2>
                <p>Vi behandlar dina personuppgifter för följande ändamål:</p>

                <div className="overflow-x-auto not-prose">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="text-left py-3 px-4 font-semibold text-gray-900">Ändamål</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900">
                          Laglig grund
                        </th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900">
                          Lagringstid
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      <tr>
                        <td className="py-3 px-4 text-gray-700">Tillhandahålla tjänsten</td>
                        <td className="py-3 px-4 text-gray-600">Avtal</td>
                        <td className="py-3 px-4 text-gray-600">Under avtalsperioden + 7 år</td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 text-gray-700">Skapa och hantera konto</td>
                        <td className="py-3 px-4 text-gray-600">Avtal</td>
                        <td className="py-3 px-4 text-gray-600">Till kontot avslutas + 30 dagar</td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 text-gray-700">Generera årsredovisningar</td>
                        <td className="py-3 px-4 text-gray-600">Avtal</td>
                        <td className="py-3 px-4 text-gray-600">7 år (bokföringslagen)</td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 text-gray-700">Fakturering och betalning</td>
                        <td className="py-3 px-4 text-gray-600">Avtal/Rättslig förpliktelse</td>
                        <td className="py-3 px-4 text-gray-600">7 år (bokföringslagen)</td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 text-gray-700">Kundservice och support</td>
                        <td className="py-3 px-4 text-gray-600">Berättigat intresse</td>
                        <td className="py-3 px-4 text-gray-600">2 år efter ärendets avslut</td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 text-gray-700">Förbättra tjänsten</td>
                        <td className="py-3 px-4 text-gray-600">Berättigat intresse</td>
                        <td className="py-3 px-4 text-gray-600">Aggregerad/anonymiserad</td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 text-gray-700">Marknadsföring (nyhetsbrev)</td>
                        <td className="py-3 px-4 text-gray-600">Samtycke</td>
                        <td className="py-3 px-4 text-gray-600">Till samtycket återkallas</td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 text-gray-700">
                          Säkerhet och missbruksförebyggande
                        </td>
                        <td className="py-3 px-4 text-gray-600">Berättigat intresse</td>
                        <td className="py-3 px-4 text-gray-600">30 dagar (loggar)</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              <section id="delning">
                <h2>4. Hur vi Delar dina Uppgifter</h2>

                <h3>4.1 Tjänsteleverantörer</h3>
                <p>Vi delar uppgifter med följande kategorier av leverantörer:</p>
                <ul>
                  <li>
                    <strong>Hosting och infrastruktur:</strong> Vercel, Railway/Render, Neon
                    (databas), Upstash (cache) - alla med datacenter inom EU/EES
                  </li>
                  <li>
                    <strong>Betalning:</strong> Stripe (PCI DSS-certifierad)
                  </li>
                  <li>
                    <strong>E-post:</strong> För transaktionsmail och notifikationer
                  </li>
                  <li>
                    <strong>Analys:</strong> Anonymiserad användningsstatistik
                  </li>
                </ul>

                <h3>4.2 Myndigheter</h3>
                <p>
                  Vi kan lämna ut uppgifter till myndigheter om vi är skyldiga enligt lag, t.ex. vid
                  begäran från Skatteverket eller domstol.
                </p>

                <h3>4.3 Vi säljer aldrig dina uppgifter</h3>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 not-prose">
                  <p className="text-green-800 font-medium">
                    Vi säljer, hyr ut eller delar aldrig dina personuppgifter med tredje part för
                    deras marknadsföringsändamål.
                  </p>
                </div>
              </section>

              <section id="tredjeland">
                <h2>5. Överföring till Tredje Land</h2>
                <p>
                  Vi strävar efter att lagra alla data inom EU/EES. Om data överförs till länder
                  utanför EU/EES (t.ex. genom vissa molntjänster) säkerställer vi att lämpliga
                  skyddsåtgärder finns, såsom:
                </p>
                <ul>
                  <li>EU-kommissionens standardavtalsklausuler (SCC)</li>
                  <li>Certifiering enligt EU-U.S. Data Privacy Framework</li>
                </ul>
              </section>

              <section id="rattigheter">
                <h2>6. Dina Rättigheter</h2>
                <p>Enligt GDPR har du följande rättigheter:</p>

                <div className="grid md:grid-cols-2 gap-4 not-prose my-6">
                  {[
                    {
                      title: "Rätt till tillgång",
                      description:
                        "Du har rätt att få bekräftelse på om vi behandlar dina personuppgifter och tillgång till uppgifterna.",
                    },
                    {
                      title: "Rätt till rättelse",
                      description:
                        "Du har rätt att få felaktiga personuppgifter korrigerade utan onödigt dröjsmål.",
                    },
                    {
                      title: "Rätt till radering",
                      description:
                        "Du har rätt att begära radering av dina uppgifter under vissa omständigheter.",
                    },
                    {
                      title: "Rätt till begränsning",
                      description:
                        "Du har rätt att begära att behandlingen av dina uppgifter begränsas.",
                    },
                    {
                      title: "Rätt till dataportabilitet",
                      description:
                        "Du har rätt att få ut dina uppgifter i ett maskinläsbart format.",
                    },
                    {
                      title: "Rätt att göra invändningar",
                      description:
                        "Du har rätt att invända mot behandling som grundar sig på berättigat intresse.",
                    },
                    {
                      title: "Rätt att återkalla samtycke",
                      description:
                        "Om behandlingen grundar sig på samtycke kan du när som helst återkalla det.",
                    },
                    {
                      title: "Rätt att klaga",
                      description:
                        "Du har rätt att klaga till Integritetsskyddsmyndigheten (IMY).",
                    },
                  ].map((right, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-semibold text-gray-900 mb-1">{right.title}</h4>
                      <p className="text-sm text-gray-600">{right.description}</p>
                    </div>
                  ))}
                </div>

                <p>
                  <strong>Kontakt för dataskyddsfrågor:</strong>{" "}
                  <a href={`mailto:${company.email}`} className="text-blue-600 hover:underline">
                    {company.email}
                  </a>
                </p>
              </section>

              <section id="cookies">
                <h2>7. Cookies</h2>

                <h3>7.1 Vad är cookies?</h3>
                <p>
                  Cookies är små textfiler som lagras på din enhet när du besöker vår webbplats. Läs
                  mer i vår{" "}
                  <Link to="/cookies" className="text-blue-600 hover:underline">
                    fullständiga cookie policy
                  </Link>
                  .
                </p>

                <h3>7.2 Vilka cookies vi använder</h3>
                <div className="overflow-x-auto not-prose">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="text-left py-3 px-4 font-semibold text-gray-900">
                          Kategori
                        </th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900">Syfte</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900">
                          Lagringstid
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      <tr>
                        <td className="py-3 px-4 text-gray-700">Nödvändiga</td>
                        <td className="py-3 px-4 text-gray-600">
                          Inloggning, säkerhet, sessionshantering
                        </td>
                        <td className="py-3 px-4 text-gray-600">Session / 24 timmar</td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 text-gray-700">Funktionella</td>
                        <td className="py-3 px-4 text-gray-600">Spara preferenser, språkval</td>
                        <td className="py-3 px-4 text-gray-600">1 år</td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 text-gray-700">Analytiska</td>
                        <td className="py-3 px-4 text-gray-600">Förstå användning (anonymiserad)</td>
                        <td className="py-3 px-4 text-gray-600">2 år</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <h3>7.3 Hantera cookies</h3>
                <p>
                  Du kan hantera cookies i din webbläsares inställningar. Observera att om du
                  blockerar nödvändiga cookies kan vissa funktioner sluta fungera.
                </p>
              </section>

              <section id="sakerhet">
                <h2>8. Säkerhet</h2>
                <p>Vi skyddar dina personuppgifter med tekniska och organisatoriska åtgärder:</p>
                <ul>
                  <li>Kryptering av data vid överföring (TLS 1.3) och lagring (AES-256)</li>
                  <li>Åtkomstkontroll och behörighetsbegränsningar</li>
                  <li>Regelbundna säkerhetsgranskningar och penetrationstester</li>
                  <li>Automatisk backup av data</li>
                  <li>Loggning och övervakning av åtkomst</li>
                  <li>Lösenord lagras med säker hashning (bcrypt)</li>
                </ul>
              </section>

              <section id="minderariga">
                <h2>9. Minderåriga</h2>
                <p>
                  Tjänsten riktar sig till företagsrepresentanter och är inte avsedd för personer
                  under 18 år. Vi samlar inte medvetet in personuppgifter från minderåriga.
                </p>
              </section>

              <section id="andringar">
                <h2>10. Ändringar av Integritetspolicyn</h2>
                <p>
                  Vi kan uppdatera denna integritetspolicy från tid till annan. Vid väsentliga
                  ändringar meddelar vi dig via e-post eller i tjänsten. Den aktuella versionen
                  finns alltid tillgänglig på denna sida.
                </p>
              </section>

              <section id="kontakt">
                <h2>11. Kontakta Oss</h2>
                <p>
                  Om du har frågor om denna integritetspolicy eller vår behandling av dina
                  personuppgifter, kontakta oss:
                </p>
                <div className="bg-gray-50 rounded-lg p-4 not-prose">
                  <p className="text-gray-700">
                    <strong>E-post:</strong>{" "}
                    <a href={`mailto:${company.email}`} className="text-blue-600 hover:underline">
                      {company.email}
                    </a>
                  </p>
                  <p className="text-gray-700 mt-2">
                    <strong>Adress:</strong> {company.name}, {formattedCityOnly}
                  </p>
                </div>
              </section>

              <hr className="my-8" />

              <div className="bg-blue-50 rounded-lg p-4 not-prose">
                <p className="text-sm text-blue-800">
                  Denna integritetspolicy gäller från {lastUpdated}. Se våra{" "}
                  <Link to="/villkor" className="text-blue-600 hover:underline">
                    Användarvillkor
                  </Link>{" "}
                  för övriga villkor som gäller för tjänsten.
                </p>
              </div>
            </article>
          </div>
        </div>
      </section>
    </>
  );
}
